// @ts-strict-ignore This file is in the process of being migrated to Typescript, but we have to go about it iteratively
// Please be cautious and fix typing issues in the code you update here.
import { createActions, createReducer } from 'reduxsauce'
import Immutable, { type DeepPartial, type Immutable as TImmutable } from 'seamless-immutable'
import {
  generateBasicPost,
  isPatientVisiblePost,
  isReplyPost,
  isValidPostData,
  normalizePostActions,
  updateEpisodeWithArchivableStatus,
} from 'APP/Lib/MessagingHelpers'
import {
  type ArchiveEpisodePayload,
  type CancelAppointmentPayload,
  type Channel,
  type CreateEpisodeSuccessPayload,
  type Episode,
  type FetchChannelsPayload,
  type FetchChannelsSuccessPayload,
  type FetchEpisodePayload,
  type FetchEpisodeSuccessPayload,
  type FetchPostsSuccessPayload,
  type FetchPractitionerBioPayload,
  FileUploadStatusEnum,
  type GoBackInEpisodePayload,
  type HistoryState,
  type ReceivePostsPayload,
  type RemovePendingPostPayload,
  type RemoveUploadedFilePayload,
  type RescheduleAppointmentPayload,
  type RetryPostPayload,
  type SetFailedPostErrorPayload,
  type SetInAppNotificationDataPayload,
  type SubmitPostFailurePayload,
  type SubmitPostPayload,
  type SubmitPostSuccessPayload,
  type UpdateChannelLastViewedPayload,
} from 'APP/Store/History/types'
import type {
  EnrichedPost,
  NewPendingPostData,
  NormalizedPostList,
  NormalizedPostListOrError,
} from 'APP/Lib/MessagingHelpers/types'
import type {
  ConversationChannel,
  ConversationEpisode,
  ConversationMember,
} from 'APP/Services/Silkroad/types'

const EPISODE_DEFAULT_STATE = {
  archived: null,
  goBack: {
    error: null,
    running: false,
  },
}

const actionDefs = {
  addDraftPhoto: ['channelId', 'photoData'],
  changeDraftText: ['channelId', 'newText'],
  cleanChannels: null,
  clearDraft: ['channelId'],
  getOrCreateIcbtEpisodeRequest: null,
  createEpisodeSuccess: ['episode', 'commandPost', 'locationPermission', 'flowType'],
  createEpisodeFailure: ['error'],
  fetchEncountersSuccess: ['encounters'],
  fetchEpisodeFailure: ['error'],
  fetchOneEpisodeDeeplinkRequest: ['episodeId', 'requestId'],
  fetchOneEpisodeRequest: ['channelId'],
  fetchOneEpisodeSuccess: ['episode'],
  fetchEpisodeMembersRequest: ['channelId'],
  fetchEpisodeMembersSuccess: ['members'],
  fetchEpisodeRequest: null,
  fetchEpisodeSuccess: ['episodes'],
  fetchChannelsRequest: ['bootstrap'],
  fetchChannelsSuccess: ['channelSets', 'memberEpisodes'],
  fetchChannelsFailure: ['error'],
  fetchPostsFailure: ['channelId', 'error'],
  fetchPostsSuccess: ['channelId', 'postData', 'fullFetch'],
  fetchNewPostsForChannel: ['channelId', 'backgroundFetch'],
  fetchOlderPostsForChannel: ['channelId'],
  fetchAllPostsForChannel: ['channelId'],
  fetchPractitionersFailure: ['error'],
  fetchPractitionersRequest: null,
  fetchPractitionersSuccess: ['practitioners'],

  fetchPractitionerBio: ['userAppId', 'profileId'],
  fetchPractitionerBioFailure: ['error'],
  fetchPractitionerBioRequest: null,
  fetchPractitionerBioSuccess: ['practitionerBio'],

  patientHistoryClear: null,
  receiveEvent: ['event'],
  receivePosts: ['channelId', 'postData', 'fetched'],
  removePosts: ['channelId', 'postIds'],
  removeDraftPhoto: ['channelId', 'photoId'],
  resetChannels: ['channelIds'],
  retryPostRequest: ['channelId', 'postId'],
  launchEpisodeCommand: ['command', 'healthIssueTypeId', 'flowType'],
  shownLocationNotSharedBar: ['channelId'],
  submitAnswerPostRequest: ['channelId', 'userId', 'answer', 'timeStamp', 'flags'],
  submitTextWithPayloadPostRequest: [
    'channelId',
    'userId',
    'text',
    'timeStamp',
    'flags',
    'payload',
  ],
  submitFilePostRequest: ['channelId', 'userId', 'fileObj', 'timeStamp', 'flags'],
  submitPostFailure: ['channelId', 'localId', 'message'],
  submitPostRequest: ['channelId', 'userId'],
  submitPostSuccess: ['channelId', 'post'],
  submitTextPostRequest: ['channelId', 'userId', 'text', 'timeStamp'],
  updateChannelError: ['channelId', 'error'],
  updateChannelState: ['channelId', 'channelState'],
  updateChannelHeader: ['channelId', 'header'],
  updateChannelLastViewed: ['channelId'],
  setInternalNavigationEpisodeId: ['episodeId'],
  setMattermostLongPollRunning: ['running'],
  mattermostSocketClose: null,
  mattermostSocketError: null,
  mattermostSocketReady: null,
  mattermostSocketDestroyed: null,
  mattermostStart: null,
  mattermostStop: null,
  mattermostLongPollStart: null,
  setInAppNotificationData: ['channelId', 'postId'],
  setAllOlderPostsFetched: ['channelId', 'allOlderPostsFetched'],
  setHasUnreadChannels: ['hasUnreads'],
  setFailedPostError: ['channelId', 'showPostFailureError'],
  getOrCreateWbiEpisodeRequest: null,
  refreshAppointmentsRequest: null,
  fetchAppointmentsRequest: null,
  fetchAppointmentsSuccess: ['appointments'],
  fetchAppointmentsFailure: ['error'],
  rescheduleAppointmentRequest: ['appointment'],
  rescheduleAppointmentSuccess: null,
  rescheduleAppointmentFailure: ['error'],
  cancelAppointmentFromCalendarRequest: ['appointment'],
  cancelAppointmentFromCalendarSuccess: null,
  cancelAppointmentFromCalendarFailure: ['error'],
  resetCancelAppointmentFromCalendar: [],
  cancelAppointmentRequest: ['appointment'],
  cancelAppointmentSuccess: null,
  cancelAppointmentFailure: ['error'],
  navigateToLink: ['link', 'context'],
  navigateToLinkSuccess: [],
  navigateToLinkError: ['error'],
  removePendingPost: ['channelId', 'postId'],
  setTypingEventData: ['event'],
  resetTypingEventData: null,

  archiveEpisodeRequest: ['channelId', 'sprigSurveyData'],
  archiveEpisodeSuccess: null,
  archiveEpisodeError: ['error'],

  setPostAsUndone: ['postId'],
  goBackInEpisodeRequest: ['channelId', 'postId'],
  goBackInEpisodeSuccess: ['channelId', 'postId'],
  goBackInEpisodeError: ['channelId', 'postId', 'error'],

  fileUploadStarted: ['channelId'],
  fileUploadCompleted: ['channelId', 'fileInfo'],
  fileUploadFailed: ['channelId', 'error'],
  fileUploadClear: ['channelId'],
  uploadFileRequest: ['channelId', 'file'],
  removeUploadedFileRequest: ['channelId', 'fileId'],
  removeUploadedFileSuccess: ['channelId'],
  removeUploadedFileFailure: ['channelId', 'error'],
  setIgnoreAppStateChanges: ['shouldIgnore'],
} as const satisfies Record<string, string[] | null>

type TActions = typeof actionDefs
type TPayloads = {
  archiveEpisodeRequest: ArchiveEpisodePayload
  createEpisodeSuccess: CreateEpisodeSuccessPayload
  cancelAppointmentRequest: CancelAppointmentPayload
  fetchChannelsRequest: FetchChannelsPayload
  fetchChannelsSuccess: FetchChannelsSuccessPayload
  fetchEpisodeRequest: FetchEpisodePayload
  fetchEpisodeSuccess: FetchEpisodeSuccessPayload
  fetchPostsSuccess: FetchPostsSuccessPayload
  fetchPractitionerBioRequest: FetchPractitionerBioPayload
  goBackInEpisodeRequest: GoBackInEpisodePayload
  receivePosts: ReceivePostsPayload
  removePendingPost: RemovePendingPostPayload
  removeUploadedFileRequest: RemoveUploadedFilePayload
  rescheduleAppointmentRequest: RescheduleAppointmentPayload
  retryPostRequest: RetryPostPayload
  setInAppNotificationData: SetInAppNotificationDataPayload
  setFailedPostError: SetFailedPostErrorPayload
  submitAnswerPostRequest: SubmitPostPayload
  submitFilePostRequest: SubmitPostPayload
  submitPostFailure: SubmitPostFailurePayload
  submitPostSuccess: SubmitPostSuccessPayload
  submitTextPostRequest: SubmitPostPayload
  submitTextWithPayloadPostRequest: SubmitPostPayload
  updateChannelLastViewed: UpdateChannelLastViewedPayload
}

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

type ImmutableHistoryState = TImmutable<HistoryState>

export const INITIAL_STATE = Immutable<HistoryState>({
  channels: {},
  hasUnreadChannels: false,
  createEpisodeError: null,
  createEpisodeRunning: false,
  currentChannelId: null,
  internalNavigationEpisodeId: null,
  encounters: {},
  completedCarePlans: [],
  episodes: {},
  episode: {},
  episodeMembers: {},
  fetchEpisodeRequestRunning: false,
  fetchEpisodeError: null,
  fetchChannelsRunning: false,
  fetchChannelsError: null,
  practitioners: {},
  fetchPractitionersError: null,
  fetchPractitionersRunning: false,
  orderedEpisodes: [],
  undonePostIds: [],
  posts: {},
  practitionerBio: {},
  fetchPractitionerBioError: null,
  fetchPractitionerBioRunning: false,
  mmSocketNotReady: true,
  inAppNotificationPostId: null,
  failedPostCounters: {},
  hasPostFailureError: false,
  appointments: {
    past: [],
    upcoming: [],
  },
  fetchAppointmentsRunning: false,
  fetchAppointmentsError: null,
  rescheduleAppointmentRunning: false,
  rescheduleAppointmentError: null,
  cancelAppointmentRunning: false,
  cancelAppointmentError: null,
  cancelAppointmentFromCalendarFailure: null,
  cancelAppointmentFromCalendarRunning: false,
  cancelAppointmentFromCalendarSuccess: false,
  link: null,
  linkContext: null,
  linkError: null,
  typingEvent: null,
  archiveEpisodeRunning: false,
  archiveEpisodeError: null,
  fileUploads: {},
  uploads: {},
  shouldIgnoreAppStateChanges: false,
  mattermostLongPollRunning: false,
})

export const createEmptyChannel = (channelId: string): Channel => ({
  channelId,
  order: [],
  posts: {},
  draft: {},
  pendingPosts: {},
  pendingOrder: [],
  lastFetchedPostId: null,
  lastFetchedPostAt: null,
  fetchingPosts: false,
  fetchingPostsError: null,
  error: null,
  missingData: false, // Can be resolved only by refetching the whole channel
  msgCount: 0,
  allMembers: [],
  lastViewedAt: 0,
})

export const buildInitialFilePayload = (data) => {
  return {
    fileName: data.fileName,
    fileType: data.fileType,
    ...(typeof data.localFile === 'string'
      ? { fileUri: data.localFile } // mobile
      : { file: data.localFile }), // web
  }
}

export const fetchEpisodeRequest = (
  state: ImmutableHistoryState,
  _payload: FetchEpisodePayload
) => {
  return state.merge({ fetchEpisodeRequestRunning: true })
}

export const fetchEpisodeSuccess = (
  state: ImmutableHistoryState,
  { episodes }: FetchEpisodeSuccessPayload
) => {
  if (episodes) {
    const newEpisodes: Record<string, Episode> = {}
    const newChannels: Record<string, Channel> = {}

    episodes.forEach((episode) => {
      const id = episode.id

      newEpisodes[id] = {
        id,
        channelId: id,
        // we get a utc string from silkroad, so we need to convert it to a timestamp
        createdAt: new Date(episode.created_at).getTime(),
        serviceId: episode.service_id ?? undefined,
        healthIssueTypeId: episode.health_issue_type_id ?? undefined,
        botStatus: episode.bot_status ?? undefined,
        outcomeId: episode.outcome_id ?? undefined,
        isArchivable: episode.is_archivable,
        ...EPISODE_DEFAULT_STATE,
      }

      if (!state.channels[id]) {
        newChannels[id] = createEmptyChannel(id)
      }
    })

    return withOrderedEpisodes(
      state.merge({
        episodes: newEpisodes,
        channels: state.channels.merge(newChannels),
        completedCarePlans: filterCompletedCarePlans(state.encounters, newEpisodes),
        fetchEpisodeRequestRunning: false,
      })
    )
  }

  return state
}

export const fetchEpisodeFailure = (state: ImmutableHistoryState) => {
  return state.merge({ fetchEpisodeRequestRunning: false })
}

export const fetchOneEpisodeSuccess = (state: ImmutableHistoryState, { episode }) => {
  if (episode && episode.id) {
    const existingEpisode = state.episodes[episode.id]

    if (existingEpisode) {
      const updatedEpisode = {
        ...existingEpisode,
        outcomeId: episode.outcomeId,
      }

      return state.merge({
        episodes: {
          ...state.episodes,
          [existingEpisode.id]: updatedEpisode,
        },
        episode: episode,
      })
    }
  }

  return state.merge({ episode })
}

export const getOrCreateIcbtEpisodeRequest = (state: ImmutableHistoryState) => {
  return state.merge({ createEpisodeRunning: true, createEpisodeError: null })
}

export const createEpisodeSuccess = (
  state: ImmutableHistoryState,
  { episode, commandPost, flowType }: CreateEpisodeSuccessPayload
) => {
  const newEpisode: Episode = {
    id: episode.id,
    channelId: episode.id,
    serviceId: episode.service_id,
    createdAt: episode.created_at,
    botStatus: episode.bot_status,
    healthIssueTypeId: episode.health_issue_type_id,
    outcomeId: episode.outcome_id,
    isArchivable: episode?.is_archivable,
    commandPost,
    flowType,
    ...EPISODE_DEFAULT_STATE,
  }
  const channel = createEmptyChannel(episode.id)

  return withOrderedEpisodes(
    state.merge({
      episodes: {
        ...state.episodes,
        [newEpisode.id]: newEpisode,
      },
      orderedEpisodes: [episode.id, ...state.orderedEpisodes],
      channels: {
        ...state.channels,
        [episode.id]: channel,
      },
      createEpisodeRunning: false,
      createEpisodeError: null,
    })
  )
}

export const createEpisodeFailure = (state: ImmutableHistoryState, { error }) =>
  state.merge({
    createEpisodeRunning: false,
    createEpisodeError: error,
  })

export const fetchPractitionersRequest = (state: ImmutableHistoryState) => {
  return state.merge({ fetchPractitionersRunning: true, fetchPractitionersError: null })
}

export const fetchPractitionersSuccess = (state: ImmutableHistoryState, { practitioners }) => {
  return state.merge({ fetchPractitionersRunning: false, practitioners })
}

export const fetchPractitionersFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ fetchPractitionersRunning: false, fetchPractitionersError: error })
}

export const fetchPractitionerBioRequest = (
  state: ImmutableHistoryState,
  _payload: FetchPractitionerBioPayload
) => {
  return state.merge({ fetchPractitionerBioRunning: true, fetchPractitionerBioError: null })
}

export const fetchPractitionerBioSuccess = (state: ImmutableHistoryState, { practitionerBio }) => {
  return state.merge({
    fetchPractitionerBioError: null,
    fetchPractitionerBioRunning: false,
    practitionerBio,
  })
}

export const fetchPractitionerBioFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ fetchPractitionerBioRunning: false, fetchPractitionerBioError: error })
}

export const extractLastPost = (postData: NormalizedPostList) => {
  const postId = postData.order[0]
  return postData.posts[postId]
}

export const extractOldestPost = (postData: NormalizedPostList) => {
  const postIndex = postData.order.length - 1
  const postId = postData.order[postIndex]
  return postData.posts[postId]
}

export const extractPostInfo = (
  channel: TImmutable<Channel> | undefined,
  postData: NormalizedPostListOrError | undefined,
  fetched: boolean | undefined
): Partial<Channel> | null => {
  if (!fetched || !channel || !isValidPostData(postData)) return null
  const result: Partial<Channel> = {}
  const lastPost = extractLastPost(postData)
  if (!channel.lastFetchedPostAt || channel.lastFetchedPostAt < lastPost.create_at) {
    result.lastFetchedPostAt = lastPost.create_at
    result.lastFetchedPostId = lastPost.id
  }

  const oldestPost = extractOldestPost(postData)
  if (!channel.oldestFetchedPostAt || channel.oldestFetchedPostAt > oldestPost.create_at) {
    result.oldestFetchedPostAt = oldestPost.create_at
    result.oldestFetchedPostId = oldestPost.id
  }

  return result
}

export const extractUpdatedPosts = (
  channel: TImmutable<Channel>,
  postData: NormalizedPostListOrError
): Record<string, EnrichedPost> => {
  const postIds = Object.keys(postData.posts).filter(
    (id) => channel.posts[id] && channel.posts[id].update_at !== postData.posts[id].update_at
  )

  const updatedPosts: Record<string, EnrichedPost> = {}
  postIds.forEach((id) => (updatedPosts[id] = normalizePostActions(postData.posts[id])))
  return updatedPosts
}

export const getNextPostOrder = (
  channel: TImmutable<Channel>,
  order: string[],
  posts: Record<string, EnrichedPost>,
  discardPrevState = false
): { visible: string[]; hidden: string[] } => {
  const nextOrder = [...order, ...((!discardPrevState && channel.order) || [])]
  nextOrder.sort((a, b) => posts[b].create_at - posts[a].create_at)
  const newData = nextOrder.reduce<{ visible: string[]; hidden: string[] }>(
    (acc, id) => {
      if (isPatientVisiblePost(posts[id])) return { ...acc, visible: acc.visible.concat(id) }
      else return { ...acc, hidden: acc.hidden.concat(id) }
    },
    { visible: [], hidden: [] }
  )
  return newData
}

export const extractAllMembers = (
  channel: TImmutable<Channel>,
  order: string[],
  posts: Record<string, EnrichedPost>
): string[] => {
  const allMembers = [...channel.allMembers]
  order.forEach((postId) => {
    const userId = posts[postId].user_id
    if (allMembers.indexOf(userId) === -1) allMembers.push(userId)
  })
  return allMembers
}

export const extractAndFilterNewPosts = (
  channel: TImmutable<Channel> | undefined,
  postData: NormalizedPostListOrError | undefined,
  discardPrevState = false
): Partial<Channel> | null => {
  try {
    if (!channel || !isValidPostData(postData)) return null

    const filteredOrder: { visible: string[]; hidden: string[] } = discardPrevState
      ? { visible: postData.order, hidden: [] }
      : postData.order.reduce<{ visible: string[]; hidden: string[] }>(
          (acc, postId) => {
            if (
              isPatientVisiblePost(postData.posts[postId]) &&
              !isReplyPost(postData.posts[postId]) &&
              channel.order.indexOf(postId) === -1
            ) {
              return { ...acc, visible: acc.visible.concat(postId) }
            } else {
              return { ...acc, hidden: acc.hidden.concat(postId) }
            }
          },
          { visible: [], hidden: [] }
        )

    const updatedPosts = extractUpdatedPosts(channel, postData)

    const posts = filteredOrder.visible.reduce<Record<string, EnrichedPost>>(
      (map, postId) => ({ ...map, [postId]: normalizePostActions(postData.posts[postId]) }),
      { ...channel.posts.asMutable({ deep: true }), ...updatedPosts }
    )

    const hiddenPosts = filteredOrder.hidden.reduce<Record<string, EnrichedPost>>(
      (map, postId) => ({ ...map, [postId]: normalizePostActions(postData.posts[postId]) }),
      { ...channel.posts.asMutable({ deep: true }), ...updatedPosts }
    )

    const order = getNextPostOrder(channel, filteredOrder.visible, posts, discardPrevState).visible

    const allMembers = extractAllMembers(channel, filteredOrder.visible, posts)

    return { order, posts, error: null, allMembers, hiddenPosts }
  } catch (error) {
    return { error: String(error) }
  }
}

export const extractFromChannelInfo = (
  channelInfo: ConversationChannel | undefined,
  episodeInfo?: ConversationEpisode
): Partial<Channel> | undefined => {
  const episodeIsActive = episodeInfo && episodeInfo.state === 'in_progress'
  return (
    channelInfo && {
      header: channelInfo.header,
      active: episodeIsActive,
      msgCount: channelInfo.total_msg_count || 0,
      updateAt: channelInfo.update_at ?? undefined,
    }
  )
}

export const extractFromMemberInfo = (
  memberInfo: ConversationMember | undefined
): Partial<Channel> | undefined =>
  memberInfo && {
    // `createEmptyChannel` starts a channel at 0, which is also what Mattermost
    // reports for a channel the member has never opened.
    lastViewedAt: memberInfo.last_viewed_at ?? 0,
  }

export const withOrderedEpisodes = (state: ImmutableHistoryState, channelId?: string) => {
  if (state.orderedEpisodes && !!channelId && state.orderedEpisodes[0] === channelId) return state
  const orderedEpisodes = Object.keys(state.episodes)
    .filter((id) => {
      const serviceId = state.episodes[id]?.serviceId
      return serviceId !== 'wellbeing_index_assessment'
    })
    .sort((a, b) => {
      const chanA = state.channels[a]
      const chanB = state.channels[b]
      const postA = chanA.order.length !== 0 && chanA.posts[chanA.order[0]]
      const postB = chanA.order.length !== 0 && chanB.posts[chanB.order[0]]
      const dateA = postA ? postA.create_at : state.episodes[a].createdAt
      const dateB = postB ? postB.create_at : state.episodes[b].createdAt
      return (dateB ?? 0) - (dateA ?? 0)
    })
  return state.merge({ orderedEpisodes })
}

export const receivePosts = (
  state: ImmutableHistoryState,
  { channelId, postData, fetched, fullFetch }: ReceivePostsPayload
) => {
  const channel = state.channels[channelId] || createEmptyChannel(channelId)

  const extractedAndFilteredNewPosts = extractAndFilterNewPosts(
    channel,
    postData,
    fullFetch && channel.missingData
  )

  const newChannel: Partial<Channel> = {
    ...extractedAndFilteredNewPosts,
    ...extractPostInfo(channel, postData, fetched),
    missingData: fullFetch || postData.missingData ? postData.missingData : channel.missingData,
  }

  const updatedEpisode = updateEpisodeWithArchivableStatus(
    extractedAndFilteredNewPosts?.hiddenPosts,
    state?.episodes?.[channelId]
  )

  const episodes = updatedEpisode ? { [channelId]: updatedEpisode } : {}

  return withOrderedEpisodes(
    state.merge(
      {
        episodes,
        channels: {
          // Casting is needed because DeepPartial produces false negatives when dealing with some nested structures
          [channelId]: newChannel as DeepPartial<Channel>,
        },
      },
      { deep: true }
    ),
    channelId
  )
}

export const removePosts = (state: ImmutableHistoryState, { channelId, postIds = [] }) => {
  const channel = state.channels[channelId] || {}
  const order = (channel.order || []).filter((id) => postIds.indexOf(id) === -1)
  return state.merge(
    {
      channels: {
        [channelId]: {
          order,
        },
      },
    },
    { deep: true }
  )
}

export const resetChannels = (state: ImmutableHistoryState, { channelIds }) => {
  if (!(channelIds instanceof Array)) {
    return state
  }
  const updatedChannels = {}
  channelIds.forEach((id) => (updatedChannels[id] = createEmptyChannel(id)))
  return state.merge({
    channels: {
      ...state.channels,
      ...updatedChannels,
    },
  })
}

/* istanbul ignore next */
export const fetchChannelsSuccess = (
  state: ImmutableHistoryState,
  { channelSets, memberEpisodes }: FetchChannelsSuccessPayload
) => {
  let i = channelSets.length
  if (i === 0) return state.merge({ fetchChannelsRunning: false, fetchChannelsError: null })

  const channels: Record<string, Partial<Channel>> = {}
  while (i > 0) {
    i--
    const set = channelSets[i]
    const channel = state.channels[set.channelId]
    const episodeInfo = memberEpisodes.find((item) => item.id === set.channelId)

    // Extract / normalize / filter
    channels[set.channelId] = {
      missingData: set.postData && 'missingData' in set.postData && set.postData.missingData,
      ...extractFromChannelInfo(set.channelInfo, episodeInfo),
      ...extractFromMemberInfo(set.memberInfo),
      ...extractPostInfo(channel, set.postData, true),
      ...extractAndFilterNewPosts(channel, set.postData),
    }
  }

  return withOrderedEpisodes(
    state.merge(
      {
        fetchChannelsRunning: false,
        fetchChannelsError: null,
        // Casting is needed because DeepPartial produces false negatives when dealing with some nested structures
        channels: channels as DeepPartial<Record<string, Channel>>,
      },
      { deep: true }
    )
  )
}

export const fetchChannelsRequest = (
  state: ImmutableHistoryState,
  _payload: FetchChannelsPayload
) => {
  return state.merge({ fetchChannelsRunning: true, fetchChannelsError: null })
}

export const fetchChannelsFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ fetchChannelsRunning: false, fetchChannelsError: String(error) })
}

export const fetchPostsSuccess = (
  state: ImmutableHistoryState,
  { channelId, postData, fullFetch }: FetchPostsSuccessPayload
) =>
  receivePosts(
    state.merge(
      {
        channels: {
          [channelId]: {
            fetchingPosts: false,
            fetchingPostsError: null,
          },
        },
      },
      { deep: true }
    ),
    { channelId, postData, fetched: true, fullFetch }
  )

export const fetchPostsRequest = (state: ImmutableHistoryState, { channelId, backgroundFetch }) =>
  state.merge(
    {
      channels: {
        [channelId]: {
          ...(!backgroundFetch && { fetchingPosts: true }),
          fetchingPostsError: null,
        },
      },
    },
    { deep: true }
  )

export const fetchPostsFailure = (state: ImmutableHistoryState, { channelId, error }) =>
  state.merge(
    {
      channels: {
        [channelId]: {
          fetchingPosts: false,
          fetchingPostsError: String(error),
        },
      },
    },
    { deep: true }
  )

const filterCompletedCarePlans = (
  encounters: HistoryState['encounters'] | TImmutable<HistoryState['encounters']>,
  episodes: Record<string, Episode>
) => {
  return Object.values(encounters)
    .filter((encounter) => {
      const status = encounter.status && encounter.status.toLowerCase().trim()
      return status === 'completed' && episodes[encounter.episode_id]
    })
    .reverse()
}

export const fetchEncountersSuccess = (state: ImmutableHistoryState, { encounters = [] }) => {
  if (!encounters) return state

  let encountersMap = {}
  encounters.forEach((encounter) => {
    encountersMap[encounter.id] = encounter
  })
  const mergedEncounters = state.encounters.merge(encountersMap)

  return state.merge(
    {
      encounters: mergedEncounters,
      completedCarePlans: filterCompletedCarePlans(mergedEncounters, state.episodes),
    },
    { deep: true }
  )
}

export const changeDraftText = (state: ImmutableHistoryState, { channelId, newText }) => {
  const channel = state.channels[channelId]
  if (channel) {
    return state.merge(
      {
        channels: {
          [channelId]: {
            draft: {
              text: newText,
            },
          },
        },
      },
      { deep: true }
    )
  } else {
    return state
  }
}

export const clearDraft = (state: ImmutableHistoryState, { channelId }) => {
  const channel = state.channels[channelId]
  if (channel) {
    return state.merge(
      {
        channels: {
          [channelId]: {
            draft: {
              text: null,
              photo: null,
              localId: null,
            },
          },
        },
      },
      { deep: true }
    )
  } else {
    return state
  }
}

/* istanbul ignore next */
export const submitPostRequest = (
  state: ImmutableHistoryState,
  { channelId, userId, answer, fileObj, text, timeStamp, flags = {}, payload }: SubmitPostPayload
) => {
  const channel = state.channels && state.channels[channelId]
  if (channel) {
    const currentPendingPosts = channel.pendingPosts || {}
    const currentPendingOrder = channel.pendingOrder || []
    const options: NewPendingPostData = {
      file: fileObj,
      message: text,
      answer: answer,
      payload: payload,
      timeStamp,
      ...flags,
    }
    return state.merge(
      {
        channels: {
          [channelId]: {
            pendingPosts: {
              ...currentPendingPosts,
              [timeStamp]: generateBasicPost(userId, options),
            },
            pendingOrder: [timeStamp, ...currentPendingOrder],
          } as DeepPartial<Channel>,
        },
      },
      { deep: true }
    )
  } else {
    return state
  }
}

export const retryPostRequest = (
  state: ImmutableHistoryState,
  { channelId, postId }: RetryPostPayload
) => {
  const channel = state.channels && state.channels[channelId]
  if (channel) {
    let pendingPosts = { ...channel.pendingPosts }
    if (pendingPosts[postId]) {
      return state.merge(
        {
          channels: {
            [channelId]: {
              pendingPosts: {
                [postId]: {
                  props: {
                    failed: false,
                  },
                },
              },
            },
          },
        },
        { deep: true }
      )
    }
  }
  return state
}

export const removePendingPost = (
  state: ImmutableHistoryState,
  { channelId, postId }: RemovePendingPostPayload
) => {
  const channel = state.channels && state.channels[channelId]
  if (channel && channel.pendingPosts[postId]) {
    let pendingPosts = { ...channel.pendingPosts }
    const pendingOrder = channel.pendingOrder.filter((id) => id !== postId)
    delete pendingPosts[postId]
    return state.merge(
      {
        channels: {
          [channelId]: {
            pendingPosts,
            pendingOrder,
          },
        },
      },
      { deep: true }
    )
  }
  return state
}

export const setFailedPostError = (
  state: ImmutableHistoryState,
  { channelId, showPostFailureError }: SetFailedPostErrorPayload
) => {
  const channel = state.channels && state.channels[channelId]
  if (channel) {
    return state.merge(
      {
        channels: {
          [channelId]: {
            hasPostFailureError: showPostFailureError,
          },
        },
      },
      { deep: true }
    )
  }
  return state
}

/*
  Note:

  There's a race condition between this message post and the fetch posts long poll.
  When the posts long poll wins, we don't need to add the post anymore as it has already been added.
  Instead, we just remove the pending post and get on with our lives.

  And so it goes.
*/
export const submitPostSuccess = (
  state: ImmutableHistoryState,
  { channelId, post }: SubmitPostSuccessPayload
) => {
  const channel = state.channels[channelId]
  if (channel) {
    const postId = post.props && post.props.localId
    const pendingPostIndex = channel.pendingOrder.indexOf(postId)

    const { order } = channel
    const postAlreadyPresent = order.indexOf(post.id) !== -1
    let newPendingPosts = { ...channel.pendingPosts }
    let newPendingOrder = [...channel.pendingOrder]
    delete newPendingPosts[postId]
    newPendingOrder.splice(pendingPostIndex, 1)
    const lastFetchedPostId = post.id

    const nextPosts = { ...channel.posts, [post.id]: post }
    const nextOrder = postAlreadyPresent
      ? channel.order
      : [post.id, ...channel.order].sort((a, b) => nextPosts[b].create_at - nextPosts[a].create_at)

    return state.merge(
      {
        channels: {
          [channelId]: {
            posts: nextPosts,
            order: nextOrder,
            pendingPosts: newPendingPosts,
            pendingOrder: newPendingOrder,
            lastFetchedPostId,
          },
        },
      },
      { deep: true }
    )
  } else {
    return state
  }
}

export const submitPostFailure = (
  state: ImmutableHistoryState,
  { channelId, localId, message }: SubmitPostFailurePayload
) => {
  const channel = state.channels[channelId]
  if (channel) {
    const pendingPosts = channel.pendingPosts
    const pendingPost = pendingPosts[localId]

    if (pendingPost) {
      return state.merge(
        {
          channels: {
            [channelId]: {
              pendingPosts: {
                ...pendingPosts,
                [localId]: {
                  ...pendingPost,
                  props: {
                    ...pendingPost.props,
                    retryCount: (pendingPost.props.retryCount || 0) + 1,
                    errorMessage: message,
                    failed: true,
                  },
                },
              },
            } as DeepPartial<Channel>,
          },
        },
        { deep: true }
      )
    }
  }
  return state
}

export const updateChannelState = (state: ImmutableHistoryState, { channelId, channelState }) => {
  const isActive =
    channelState === undefined ||
    channelState === 'active' ||
    channelState === 'pending' ||
    channelState === 'followup'
  const isArchived = channelState === 'archived'
  return state.merge(
    {
      channels: {
        [channelId]: {
          active: isActive,
          archived: isArchived,
        },
      },
    },
    { deep: true }
  )
}

export const updateChannelHeader = (state: ImmutableHistoryState, { channelId, header }) => {
  let currentChannel = state.channels[channelId]
  if (!currentChannel) return state

  currentChannel = currentChannel.merge({
    header: header,
  })

  return state.merge(
    {
      channels: {
        [channelId]: currentChannel,
      },
    },
    { deep: true }
  )
}

export const updateChannelLastViewed = (
  state: ImmutableHistoryState,
  { channelId }: UpdateChannelLastViewedPayload
) => {
  try {
    if (!channelId || !state.channels || !state.channels[channelId]) return state
    const channel = state.channels[channelId]
    const lastPost = channel.order && channel.posts[channel.order[0]]
    const lastViewedAt = lastPost ? lastPost.create_at + 1 : Date.now()
    return state.merge(
      {
        currentChannelId: channelId,
        channels: {
          [channelId]: {
            lastViewedAt,
          },
        },
      },
      { deep: true }
    )
  } catch (_err) {
    return state
  }
}

export const setInternalNavigationEpisodeId = (state: ImmutableHistoryState, { episodeId }) => {
  return state.merge({
    internalNavigationEpisodeId: episodeId,
  })
}

export const clear = () => INITIAL_STATE

export const cleanChannels = (state: ImmutableHistoryState) => {
  const channels = state.channels

  let cleanedChannels = { ...channels }
  Object.keys(channels).forEach((channelId) => {
    const channel = channels[channelId]
    const { pendingPosts, pendingOrder } = channel

    if (pendingPosts && pendingOrder) {
      let cleanedPendingPosts = { ...pendingPosts }
      pendingOrder.forEach((pendingPostId) => {
        const pendingPost = pendingPosts[pendingPostId]
        cleanedPendingPosts[pendingPostId] = {
          ...pendingPost,
          props: {
            ...pendingPost.props,
            failed: true,
          },
        }
      })
      cleanedChannels[channelId] = {
        ...channel,
        pendingPosts: cleanedPendingPosts,
      }
    }
  })

  return state.merge(
    {
      channels: cleanedChannels,
    },
    { deep: true }
  )
}

export const setMattermostLongPollRunning = (
  state: ImmutableHistoryState,
  { running }: { running: boolean }
) => state.merge({ mattermostLongPollRunning: running })
export const mattermostSocketClose = (state: ImmutableHistoryState) =>
  state.merge({ mmSocketNotReady: true })
export const mattermostSocketError = (state: ImmutableHistoryState) =>
  state.merge({ mmSocketNotReady: true })
export const mattermostSocketReady = (state: ImmutableHistoryState) =>
  state.merge({ mmSocketNotReady: false })
export const mattermostSocketDestroyed = (state: ImmutableHistoryState) =>
  state.merge({ mmSocketNotReady: true })
export const setInAppNotificationData = (
  state: ImmutableHistoryState,
  { channelId, postId }: SetInAppNotificationDataPayload
) => state.merge({ inAppNotificationPostId: postId, inAppNotificationChannelId: channelId })
export const setAllOlderPostsFetched = (
  state: ImmutableHistoryState,
  { channelId, allOlderPostsFetched }
) => state.merge({ channels: { [channelId]: { allOlderPostsFetched } } }, { deep: true })

export const setHasUnreadChannels = (state: ImmutableHistoryState, { hasUnreads }) =>
  state.merge({ hasUnreadChannels: hasUnreads })

export const getOrCreateWbiEpisodeRequest = (state: ImmutableHistoryState) => {
  return state.merge({ createEpisodeRunning: true, createEpisodeError: null })
}

export const fetchAppointmentsRequest = (state: ImmutableHistoryState) => {
  return state.merge({ fetchAppointmentsRunning: true, fetchAppointmentsError: null })
}

export const fetchAppointmentsSuccess = (state: ImmutableHistoryState, { appointments }) => {
  return state.merge({ fetchAppointmentsRunning: false, appointments })
}

export const fetchAppointmentsFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ fetchAppointmentsRunning: false, fetchAppointmentsError: error })
}

export const rescheduleAppointmentRequest = (
  state: ImmutableHistoryState,
  _payload: RescheduleAppointmentPayload
) => {
  return state.merge({ rescheduleAppointmentRunning: true, rescheduleAppointmentError: null })
}

export const rescheduleAppointmentSuccess = (state: ImmutableHistoryState) => {
  return state.merge({ rescheduleAppointmentRunning: false, rescheduleAppointmentError: null })
}

export const rescheduleAppointmentFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ rescheduleAppointmentRunning: false, rescheduleAppointmentError: error })
}

export const cancelAppointmentFromCalendarRequest = (state: ImmutableHistoryState) => {
  return state.merge({
    cancelAppointmentFromCalendarRunning: true,
    cancelAppointmentFromCalendarFailure: null,
    cancelAppointmentFromCalendarSuccess: false,
  })
}

export const cancelAppointmentFromCalendarSuccess = (state: ImmutableHistoryState) => {
  return state.merge({
    cancelAppointmentFromCalendarRunning: false,
    cancelAppointmentFromCalendarFailure: null,
    cancelAppointmentFromCalendarSuccess: true,
  })
}

export const cancelAppointmentFromCalendarFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({
    cancelAppointmentFromCalendarRunning: false,
    cancelAppointmentFromCalendarFailure: error,
    cancelAppointmentFromCalendarSuccess: false,
  })
}

export const resetCancelAppointmentFromCalendar = (state: ImmutableHistoryState) => {
  return state.merge({
    cancelAppointmentFromCalendarRunning: false,
    cancelAppointmentFromCalendarFailure: null,
    cancelAppointmentFromCalendarSuccess: false,
  })
}

export const cancelAppointmentRequest = (
  state: ImmutableHistoryState,
  _payload: CancelAppointmentPayload
) => {
  return state.merge({ cancelAppointmentRunning: true, cancelAppointmentError: null })
}

export const cancelAppointmentSuccess = (state: ImmutableHistoryState) => {
  return state.merge({ cancelAppointmentRunning: false, cancelAppointmentError: null })
}

export const cancelAppointmentFailure = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ cancelAppointmentRunning: false, cancelAppointmentError: error })
}

export const navigateToLink = (state: ImmutableHistoryState, { link, context }) => {
  return state.merge({ link: link, linkContext: context, linkError: null })
}

export const navigateToLinkSuccess = (state: ImmutableHistoryState) => {
  return state.merge({ link: null, linkContext: null })
}

export const navigateToLinkError = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ linkError: error })
}

export const updateEpisodeState = (state: ImmutableHistoryState, { episode }) =>
  state.merge({ episode })

export const updateEpisodeMembersState = (state: ImmutableHistoryState, { members }) =>
  state.merge({ episodeMembers: members })

export const setTypingEventData = (state: ImmutableHistoryState, { event }) =>
  state.merge({ typingEvent: event })

export const archiveEpisodeRequest = (
  state: ImmutableHistoryState,
  _payload: ArchiveEpisodePayload
) => {
  return state.merge({ archiveEpisodeRunning: true, archiveEpisodeError: null })
}

export const archiveEpisodeSuccess = (state: ImmutableHistoryState) => {
  return state.merge({ archiveEpisodeRunning: false, archiveEpisodeError: null })
}

export const archiveEpisodeError = (state: ImmutableHistoryState, { error }) => {
  return state.merge({ archiveEpisodeRunning: false, archiveEpisodeError: error })
}

export const setPostAsUndone = (state: ImmutableHistoryState, { postId }) =>
  state.merge(
    {
      undonePostIds: [...state.undonePostIds, postId],
    },
    { deep: true }
  )

export const goBackInEpisodeRequest = (
  state: ImmutableHistoryState,
  { channelId, postId }: GoBackInEpisodePayload
) => {
  return state.merge(
    {
      episodes: {
        [channelId]: {
          goBack: {
            error: null,
            running: true,
          },
        },
      },
      undonePostIds: [...state.undonePostIds, postId],
    },
    { deep: true }
  )
}

export const goBackInEpisodeSuccess = (state: ImmutableHistoryState, { channelId, postId }) => {
  return state.merge(
    {
      episodes: {
        [channelId]: {
          ...EPISODE_DEFAULT_STATE,
        },
      },
      undonePostIds: [...state.undonePostIds, postId],
    },
    { deep: true }
  )
}

export const goBackInEpisodeError = (
  state: ImmutableHistoryState,
  { error, channelId, postId }
) => {
  return state.merge(
    {
      episodes: {
        [channelId]: {
          goBack: {
            error: error,
            running: false,
          },
        },
      },
      undonePostIds: state.undonePostIds.filter((id) => id !== postId),
    },
    { deep: true }
  )
}

const mapUploadStatusToChannelStatus = (status) => {
  switch (status) {
    case FileUploadStatusEnum.UPLOADING:
    case FileUploadStatusEnum.REMOVING_FILE:
      return 'running'
    case FileUploadStatusEnum.UPLOADED:
      return 'done'
    case FileUploadStatusEnum.FAILED:
      return 'error'
    default:
      return null
  }
}

const setUploadState = (state: ImmutableHistoryState, channelId, updates) => {
  const previous = state.uploads?.[channelId] || {}
  const nextUploadState = {
    status: null,
    fileInfo: null,
    fileId: null,
    error: null,
    ...previous,
    ...updates,
  }

  const channelStatus = mapUploadStatusToChannelStatus(nextUploadState.status)

  return state.merge(
    {
      uploads: {
        [channelId]: nextUploadState,
      },
      fileUploads: {
        [channelId]: channelStatus,
      },
    },
    { deep: true }
  )
}

export const fileUploadStarted = (state: ImmutableHistoryState, { channelId }) => {
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.UPLOADING,
    fileInfo: null,
    fileId: null,
    error: null,
  })
}

export const fileUploadCompleted = (state: ImmutableHistoryState, { channelId, fileInfo }) => {
  const info = fileInfo ?? null
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.UPLOADED,
    fileInfo: info,
    fileId: info?.id ?? null,
    error: null,
  })
}

export const fileUploadFailed = (state: ImmutableHistoryState, { channelId, error }) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Upload failed'
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.FAILED,
    error: errorMessage,
  })
}

export const clearFileUpload = (state: ImmutableHistoryState, { channelId }) => {
  return setUploadState(state, channelId, {
    status: null,
    fileInfo: null,
    fileId: null,
    error: null,
  })
}

export const uploadFileRequestReducer = (state: ImmutableHistoryState, { channelId }) => {
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.UPLOADING,
    error: null,
    fileInfo: null,
    fileId: null,
  })
}

export const removeUploadedFileRequestReducer = (
  state: ImmutableHistoryState,
  { channelId, fileId }: RemoveUploadedFilePayload
) => {
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.REMOVING_FILE,
    fileId: fileId ?? state.uploads?.[channelId]?.fileId ?? null,
    error: null,
  })
}

export const removeUploadedFileSuccessReducer = (state: ImmutableHistoryState, { channelId }) => {
  return setUploadState(state, channelId, {
    status: null,
    fileInfo: null,
    fileId: null,
    error: null,
  })
}

export const removeUploadedFileFailureReducer = (
  state: ImmutableHistoryState,
  { channelId, error }
) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Failed to remove file'
  return setUploadState(state, channelId, {
    status: FileUploadStatusEnum.FAILED,
    error: errorMessage,
  })
}

export const setIgnoreAppStateChanges = (state: ImmutableHistoryState, { shouldIgnore }) => {
  return state.merge({ shouldIgnoreAppStateChanges: shouldIgnore })
}

export const PatientHistoryTypes = Types
export default Creators

export const reducer = createReducer<TPayloads, TActions, ImmutableHistoryState>(INITIAL_STATE, {
  [Types.CHANGE_DRAFT_TEXT]: changeDraftText,
  [Types.CLEAN_CHANNELS]: cleanChannels,
  [Types.CLEAR_DRAFT]: clearDraft,
  [Types.CREATE_EPISODE_FAILURE]: createEpisodeFailure,
  [Types.GET_OR_CREATE_ICBT_EPISODE_REQUEST]: getOrCreateIcbtEpisodeRequest,
  [Types.CREATE_EPISODE_SUCCESS]: createEpisodeSuccess,
  [Types.FETCH_ENCOUNTERS_SUCCESS]: fetchEncountersSuccess,
  [Types.FETCH_EPISODE_REQUEST]: fetchEpisodeRequest,
  [Types.FETCH_EPISODE_SUCCESS]: fetchEpisodeSuccess,
  [Types.FETCH_EPISODE_FAILURE]: fetchEpisodeFailure,
  [Types.FETCH_CHANNELS_REQUEST]: fetchChannelsRequest,
  [Types.FETCH_CHANNELS_SUCCESS]: fetchChannelsSuccess,
  [Types.FETCH_CHANNELS_FAILURE]: fetchChannelsFailure,
  [Types.FETCH_NEW_POSTS_FOR_CHANNEL]: fetchPostsRequest,
  [Types.FETCH_ALL_POSTS_FOR_CHANNEL]: fetchPostsRequest,
  [Types.FETCH_OLDER_POSTS_FOR_CHANNEL]: fetchPostsRequest,
  [Types.FETCH_POSTS_FAILURE]: fetchPostsFailure,
  [Types.FETCH_POSTS_SUCCESS]: fetchPostsSuccess,
  [Types.FETCH_PRACTITIONERS_FAILURE]: fetchPractitionersFailure,
  [Types.FETCH_PRACTITIONERS_REQUEST]: fetchPractitionersRequest,
  [Types.FETCH_PRACTITIONERS_SUCCESS]: fetchPractitionersSuccess,

  [Types.FETCH_PRACTITIONER_BIO_FAILURE]: fetchPractitionerBioFailure,
  [Types.FETCH_PRACTITIONER_BIO_REQUEST]: fetchPractitionerBioRequest,
  [Types.FETCH_PRACTITIONER_BIO_SUCCESS]: fetchPractitionerBioSuccess,
  [Types.PATIENT_HISTORY_CLEAR]: clear,
  [Types.RECEIVE_POSTS]: receivePosts,
  [Types.REMOVE_POSTS]: removePosts,
  [Types.RESET_CHANNELS]: resetChannels,
  [Types.RETRY_POST_REQUEST]: retryPostRequest,
  [Types.SUBMIT_ANSWER_POST_REQUEST]: submitPostRequest,
  [Types.SUBMIT_FILE_POST_REQUEST]: submitPostRequest,
  [Types.SUBMIT_POST_FAILURE]: submitPostFailure,
  // TODO: this seems unused, investigate & remove if so
  [Types.SUBMIT_POST_REQUEST]: submitPostRequest,
  [Types.SUBMIT_POST_SUCCESS]: submitPostSuccess,
  [Types.SUBMIT_TEXT_POST_REQUEST]: submitPostRequest,
  [Types.SUBMIT_TEXT_WITH_PAYLOAD_POST_REQUEST]: submitPostRequest,
  [Types.UPDATE_CHANNEL_STATE]: updateChannelState,
  [Types.UPDATE_CHANNEL_HEADER]: updateChannelHeader,
  [Types.UPDATE_CHANNEL_LAST_VIEWED]: updateChannelLastViewed,
  [Types.SET_MATTERMOST_LONG_POLL_RUNNING]: setMattermostLongPollRunning,
  [Types.MATTERMOST_SOCKET_CLOSE]: mattermostSocketClose,
  [Types.MATTERMOST_SOCKET_ERROR]: mattermostSocketError,
  [Types.MATTERMOST_SOCKET_READY]: mattermostSocketReady,
  [Types.MATTERMOST_SOCKET_DESTROYED]: mattermostSocketDestroyed,
  [Types.SET_IN_APP_NOTIFICATION_DATA]: setInAppNotificationData,
  [Types.SET_ALL_OLDER_POSTS_FETCHED]: setAllOlderPostsFetched,
  [Types.SET_HAS_UNREAD_CHANNELS]: setHasUnreadChannels,
  [Types.SET_FAILED_POST_ERROR]: setFailedPostError,
  [Types.GET_OR_CREATE_WBI_EPISODE_REQUEST]: getOrCreateWbiEpisodeRequest,
  [Types.FETCH_APPOINTMENTS_REQUEST]: fetchAppointmentsRequest,
  [Types.FETCH_APPOINTMENTS_SUCCESS]: fetchAppointmentsSuccess,
  [Types.FETCH_APPOINTMENTS_FAILURE]: fetchAppointmentsFailure,
  [Types.RESCHEDULE_APPOINTMENT_REQUEST]: rescheduleAppointmentRequest,
  [Types.RESCHEDULE_APPOINTMENT_SUCCESS]: rescheduleAppointmentSuccess,
  [Types.RESCHEDULE_APPOINTMENT_FAILURE]: rescheduleAppointmentFailure,
  [Types.CANCEL_APPOINTMENT_FROM_CALENDAR_REQUEST]: cancelAppointmentFromCalendarRequest,
  [Types.CANCEL_APPOINTMENT_FROM_CALENDAR_SUCCESS]: cancelAppointmentFromCalendarSuccess,
  [Types.CANCEL_APPOINTMENT_FROM_CALENDAR_FAILURE]: cancelAppointmentFromCalendarFailure,
  [Types.RESET_CANCEL_APPOINTMENT_FROM_CALENDAR]: resetCancelAppointmentFromCalendar,
  [Types.CANCEL_APPOINTMENT_REQUEST]: cancelAppointmentRequest,
  [Types.CANCEL_APPOINTMENT_SUCCESS]: cancelAppointmentSuccess,
  [Types.CANCEL_APPOINTMENT_FAILURE]: cancelAppointmentFailure,
  [Types.NAVIGATE_TO_LINK]: navigateToLink,
  [Types.NAVIGATE_TO_LINK_SUCCESS]: navigateToLinkSuccess,
  [Types.NAVIGATE_TO_LINK_ERROR]: navigateToLinkError,
  [Types.SET_INTERNAL_NAVIGATION_EPISODE_ID]: setInternalNavigationEpisodeId,
  [Types.REMOVE_PENDING_POST]: removePendingPost,
  [Types.SET_TYPING_EVENT_DATA]: setTypingEventData,
  [Types.FETCH_ONE_EPISODE_SUCCESS]: fetchOneEpisodeSuccess,
  [Types.FETCH_EPISODE_MEMBERS_SUCCESS]: updateEpisodeMembersState,
  [Types.ARCHIVE_EPISODE_REQUEST]: archiveEpisodeRequest,
  [Types.ARCHIVE_EPISODE_ERROR]: archiveEpisodeError,
  [Types.ARCHIVE_EPISODE_SUCCESS]: archiveEpisodeSuccess,
  [Types.SET_POST_AS_UNDONE]: setPostAsUndone,
  [Types.GO_BACK_IN_EPISODE_REQUEST]: goBackInEpisodeRequest,
  [Types.GO_BACK_IN_EPISODE_SUCCESS]: goBackInEpisodeSuccess,
  [Types.GO_BACK_IN_EPISODE_ERROR]: goBackInEpisodeError,
  [Types.FILE_UPLOAD_STARTED]: fileUploadStarted,
  [Types.FILE_UPLOAD_COMPLETED]: fileUploadCompleted,
  [Types.FILE_UPLOAD_FAILED]: fileUploadFailed,
  [Types.FILE_UPLOAD_CLEAR]: clearFileUpload,
  [Types.UPLOAD_FILE_REQUEST]: uploadFileRequestReducer,
  [Types.REMOVE_UPLOADED_FILE_REQUEST]: removeUploadedFileRequestReducer,
  [Types.REMOVE_UPLOADED_FILE_SUCCESS]: removeUploadedFileSuccessReducer,
  [Types.REMOVE_UPLOADED_FILE_FAILURE]: removeUploadedFileFailureReducer,
  [Types.SET_IGNORE_APP_STATE_CHANGES]: setIgnoreAppStateChanges,
})
